import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { therapistsApi } from '@/api/endpoints';
import { tokenStore } from '@/lib/auth';
import { SPECIALTIES, APPROACHES, LANGUAGES } from '@/lib/specialties';
import { PublicHeader } from '@/components/public/public-header';
import { PublicFooter } from '@/components/public/public-footer';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { NfseEligibilityBadge } from '@/components/shared/nfse-eligibility-badge';
import { ContactTherapistButton } from '@/components/shared/contact-therapist-button';
import { useRouteMeta } from '@/lib/use-route-meta';

export const Route = createFileRoute('/terapeutas/')({ component: TherapistsIndexPage });

type Modality = 'ONLINE' | 'PRESENCIAL' | 'HYBRID';
type SortBy = 'rating' | 'price_asc' | 'price_desc' | 'newest';

const SORT_LABELS: Record<SortBy, string> = {
  rating: 'Melhor avaliados',
  price_asc: 'Menor preço',
  price_desc: 'Maior preço',
  newest: 'Mais recentes',
};

const DOW_LABELS = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'];
const MONTH_LABELS = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

function TherapistsIndexPage() {
  useRouteMeta({
    title: 'Encontre terapeutas e psicólogos — perfis verificados',
    description: 'Busque profissionais por especialidade, abordagem, idioma e cidade. Agende sessões online via Google Meet ou presenciais, com pagamento integrado e seguro.',
    canonical: 'https://nossoello.com.br/terapeutas',
  });
  const [search, setSearch] = useState('');
  const [modality, setModality] = useState<Modality | ''>('');
  const [city, setCity] = useState('');
  const [selectedSpecs, setSelectedSpecs] = useState<string[]>([]);
  const [selectedApproaches, setSelectedApproaches] = useState<string[]>([]);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [sortBy, setSortBy] = useState<SortBy>('rating');
  const [filtersOpen, setFiltersOpen] = useState(false);

  const filterArgs = {
    q: search || undefined,
    modality: (modality || undefined) as Modality | undefined,
    city: city || undefined,
    specialties: selectedSpecs.length ? selectedSpecs : undefined,
    approaches: selectedApproaches.length ? selectedApproaches : undefined,
    languages: selectedLanguages.length ? selectedLanguages : undefined,
    minPrice: minPrice ? Number(minPrice) : undefined,
    maxPrice: maxPrice ? Number(maxPrice) : undefined,
    sortBy,
  };

  const { data, isLoading } = useQuery({
    queryKey: ['therapists-public', filterArgs],
    queryFn: () => therapistsApi.list(filterArgs),
  });

  const therapists = data?.therapists ?? [];
  const total = data?.total ?? 0;

  const activeFilters =
    (search ? 1 : 0) + (modality ? 1 : 0) + (city ? 1 : 0) +
    selectedSpecs.length + selectedApproaches.length + selectedLanguages.length +
    (minPrice ? 1 : 0) + (maxPrice ? 1 : 0);

  const clearFilters = () => {
    setSearch(''); setModality(''); setCity('');
    setSelectedSpecs([]); setSelectedApproaches([]); setSelectedLanguages([]);
    setMinPrice(''); setMaxPrice(''); setSortBy('rating');
  };

  const filterFields = (
    <>
      <FilterField label="Modalidade">
        <select
          value={modality}
          onChange={(e) => setModality(e.target.value as any)}
          className="w-full bg-white border border-slate-200 rounded-md h-9 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
        >
          <option value="">Todas</option>
          <option value="ONLINE">Online</option>
          <option value="PRESENCIAL">Presencial</option>
          <option value="HYBRID">Online ou Presencial</option>
        </select>
      </FilterField>

      <FilterField label="Cidade">
        <input
          value={city}
          onChange={(e) => setCity(e.target.value)}
          placeholder="Ex: São Paulo"
          className="w-full bg-white border border-slate-200 rounded-md h-9 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
        />
      </FilterField>

      <FilterField label="Faixa de valor (R$)">
        <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center">
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="Min"
            value={minPrice}
            onChange={(e) => setMinPrice(e.target.value.replace(/\D/g, ''))}
            className="w-full min-w-0 bg-white border border-slate-200 rounded-md h-9 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
          />
          <span className="text-slate-400 text-xs">até</span>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="Max"
            value={maxPrice}
            onChange={(e) => setMaxPrice(e.target.value.replace(/\D/g, ''))}
            className="w-full min-w-0 bg-white border border-slate-200 rounded-md h-9 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300"
          />
        </div>
      </FilterField>

      <FilterField label="Especialidades">
        <ChipMultiSelect options={SPECIALTIES} value={selectedSpecs} onChange={setSelectedSpecs} />
      </FilterField>

      <FilterField label="Abordagens">
        <ChipMultiSelect options={APPROACHES} value={selectedApproaches} onChange={setSelectedApproaches} />
      </FilterField>

      <FilterField label="Idiomas">
        <ChipMultiSelect options={LANGUAGES} value={selectedLanguages} onChange={setSelectedLanguages} />
      </FilterField>
    </>
  );

  return (
    <div className="min-h-screen flex flex-col bg-[#FBF8F3]">
      <PublicHeader />
      <main className="flex-1 max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-10 w-full">
        <div className="mb-4 md:mb-6">
          <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900">Encontre seu profissional</h1>
          <p className="text-slate-500 mt-1 text-sm md:text-base">
            {isLoading ? '...' : `${total} profissionais disponíveis`}
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-2 md:gap-3 mb-4 md:mb-6">
          <div className="flex-1 relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
            </svg>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome ou descrição..."
              className="w-full bg-white border border-slate-200 rounded-xl h-11 pl-10 pr-3 focus:outline-none focus:ring-2 focus:ring-teal-300"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setFiltersOpen(true)}
              className="lg:hidden inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl h-11 px-3 text-sm font-medium text-slate-700 hover:border-slate-300"
              aria-label="Abrir filtros"
            >
              <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 6h18M6 12h12M10 18h4" strokeLinecap="round" />
              </svg>
              Filtros
              {activeFilters > 0 && (
                <span className="ml-0.5 inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-coral-500 text-white text-[10px] font-bold">
                  {activeFilters}
                </span>
              )}
            </button>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortBy)}
              className="flex-1 md:flex-none bg-white border border-slate-200 rounded-xl h-11 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300 md:w-48"
              aria-label="Classificar"
            >
              {(Object.keys(SORT_LABELS) as SortBy[]).map((k) => (
                <option key={k} value={k}>{SORT_LABELS[k]}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid lg:grid-cols-[260px_1fr] gap-6">
          <aside className="hidden lg:block">
            <Card>
              <CardContent className="p-5 space-y-5">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-sm text-slate-800">Filtros</h3>
                  {activeFilters > 0 && (
                    <button onClick={clearFilters} className="text-[11px] text-coral-500 hover:underline">
                      Limpar ({activeFilters})
                    </button>
                  )}
                </div>
                {filterFields}
              </CardContent>
            </Card>
          </aside>

          {filtersOpen && (
            <div className="lg:hidden fixed inset-0 z-50 flex" role="dialog" aria-modal="true">
              <button
                type="button"
                aria-label="Fechar filtros"
                className="absolute inset-0 bg-slate-900/40"
                onClick={() => setFiltersOpen(false)}
              />
              <div className="relative ml-auto w-[86%] max-w-sm bg-white h-full shadow-2xl flex flex-col animate-in slide-in-from-right">
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
                  <h3 className="font-bold text-slate-800">Filtros</h3>
                  <div className="flex items-center gap-3">
                    {activeFilters > 0 && (
                      <button onClick={clearFilters} className="text-xs text-coral-500 hover:underline">
                        Limpar ({activeFilters})
                      </button>
                    )}
                    <button
                      onClick={() => setFiltersOpen(false)}
                      aria-label="Fechar"
                      className="size-8 inline-flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-500"
                    >
                      ✕
                    </button>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-5 space-y-5">
                  {filterFields}
                </div>
                <div className="px-5 py-3 border-t border-slate-100 shrink-0">
                  <button
                    onClick={() => setFiltersOpen(false)}
                    className="w-full bg-teal-500 hover:bg-teal-600 text-white font-semibold h-11 rounded-full transition"
                  >
                    Ver {isLoading ? '...' : `${total} resultado${total === 1 ? '' : 's'}`}
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-5">
            {isLoading ? (
              <p className="text-slate-500">Carregando...</p>
            ) : therapists.length === 0 ? (
              <Card>
                <CardContent className="p-10 text-center text-slate-500">
                  Nenhum profissional encontrado com esses filtros.
                </CardContent>
              </Card>
            ) : (
              therapists.map((t) => <TherapistCard key={t.id} t={t} />)
            )}
          </div>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2 block">{label}</label>
      {children}
    </div>
  );
}

function ChipMultiSelect({
  options, value, onChange,
}: {
  options: readonly string[];
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const toggle = (opt: string) =>
    onChange(value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt]);
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => {
        const isOn = value.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            className={cn(
              'text-[11px] px-2.5 py-1 rounded-full border transition',
              isOn
                ? 'bg-coral-100 text-coral-700 border-coral-200'
                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300',
            )}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

const PAGE_SIZE = 5;

function TherapistCard({ t }: { t: any }) {
  const isLoggedIn = !!tokenStore.getAccess();
  const [page, setPage] = useState(0);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

  const allDays = useMemo(() => (t.calendarDays ?? []) as { date: string; slots: string[] }[], [t]);
  const totalPages = Math.max(1, Math.ceil(allDays.length / PAGE_SIZE));
  const days = allDays.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const canPrev = page > 0;
  const canNext = page < totalPages - 1;

  const formatTime = (iso: string) =>
    new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' });

  const agendarHref = selectedSlot
    ? `/terapeutas/${t.id}/agendar?slot=${encodeURIComponent(selectedSlot)}`
    : `/terapeutas/${t.id}/agendar`;

  // Se não logado, redireciona pro login mantendo o slot via `next`
  const linkTo = isLoggedIn
    ? agendarHref
    : `/login?next=${encodeURIComponent(agendarHref)}`;

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        <div className="grid lg:grid-cols-[1fr_1fr]">
          <div className="p-4 md:p-6 flex flex-col">
            <div className="flex gap-4 mb-4">
              <Link to="/terapeutas/$id" params={{ id: t.id }} className="shrink-0 relative">
                {t.avatarUrl || t.user?.image ? (
                  <img
                    src={t.avatarUrl ?? t.user.image}
                    alt={t.user?.name}
                    className="size-20 rounded-full object-cover ring-2 ring-teal-100"
                  />
                ) : (
                  <div className="size-20 rounded-full bg-gradient-to-br from-teal-300 to-teal-500 flex items-center justify-center text-white font-bold text-2xl">
                    {(t.user?.name ?? '?').charAt(0).toUpperCase()}
                  </div>
                )}
                <span className="absolute bottom-0 right-0 size-4 bg-green-500 border-2 border-white rounded-full" aria-hidden />
              </Link>
              <div className="flex-1 min-w-0">
                <Link to="/terapeutas/$id" params={{ id: t.id }} className="group">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-lg text-slate-800 group-hover:text-teal-600 leading-tight">
                      {t.user?.name}
                    </h3>
                    {t.plan === 'PRO' && <span className="text-[10px] bg-teal-500 text-white px-2 py-0.5 rounded font-bold">PRO</span>}
                    {t.plan === 'PREMIUM' && <span className="text-[10px] bg-gradient-to-r from-amber-400 to-amber-600 text-white px-2 py-0.5 rounded font-bold">PREMIUM</span>}
                  </div>
                  {/* FB-3 — sub-especializações no lugar do rótulo genérico "Profissional".
                      Fallback: se não cadastrou tags, mostra label da modalidade ou "Profissional". */}
                  {t.specializations && t.specializations.length > 0 ? (
                    <p className="text-sm text-teal-600 mt-0.5 font-medium leading-snug">
                      {t.specializations.slice(0, 3).join(' · ')}
                    </p>
                  ) : (
                    <p className="text-sm text-slate-500 mt-0.5">Profissional</p>
                  )}
                </Link>
              </div>
            </div>

            {t.specialties?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {t.specialties.slice(0, 3).map((s: string) => (
                  <span key={s} className="text-[10px] px-2 py-0.5 rounded-full bg-coral-50 text-coral-700 border border-coral-100">{s}</span>
                ))}
                {t.specialties.length > 3 && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-500">+{t.specialties.length - 3}</span>
                )}
              </div>
            )}

            <div className="mb-3">
              <NfseEligibilityBadge professionalCategory={t.professionalCategory} size="sm" />
            </div>

            {t.bio && <p className="text-sm text-slate-600 line-clamp-3 leading-relaxed flex-1">{t.bio}</p>}

            <div className="flex items-center gap-4 mt-4 text-sm">
              {t.ratingCount > 0 && (
                <div className="flex items-center gap-1.5">
                  <span className="text-amber-400">★</span>
                  <span className="font-bold text-slate-800">{Number(t.ratingAvg).toFixed(1)}</span>
                  <span className="text-slate-500">({t.ratingCount} comentários)</span>
                </div>
              )}
              {t.sessionsCount > 0 && (
                <div className="text-slate-500">{t.sessionsCount} atendimentos</div>
              )}
            </div>

            <div className="flex items-center justify-between mt-5 pt-4 border-t border-slate-100">
              <div className="text-sm text-slate-600">Sessão {t.sessionDuration ?? 50} min</div>
              <div className="bg-teal-50 text-teal-700 font-bold px-3 py-1 rounded-full text-sm border border-teal-100">
                R$ {Number(t.sessionPrice).toFixed(0)}
              </div>
            </div>
          </div>

          <div className="bg-slate-50/50 p-4 md:p-5 flex flex-col">
            <div className="flex items-stretch gap-1 mb-3">
              <button
                aria-label="Dias anteriores"
                disabled={!canPrev}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="px-1.5 rounded-md bg-coral-50 text-coral-500 disabled:opacity-30 hover:bg-coral-100 transition"
              >‹</button>
              <div className="flex-1 grid grid-cols-5 gap-1">
                {days.map((d) => {
                  const date = new Date(d.date);
                  return (
                    <div key={d.date} className="text-center py-2 px-1 border border-slate-200 rounded-md bg-white">
                      <div className="text-[9px] font-bold text-slate-400">{DOW_LABELS[date.getUTCDay()]}</div>
                      <div className="text-lg font-extrabold text-slate-800 leading-tight">{date.getUTCDate()}</div>
                      <div className="text-[9px] font-bold text-slate-400">{MONTH_LABELS[date.getUTCMonth()]}</div>
                    </div>
                  );
                })}
              </div>
              <button
                aria-label="Próximos dias"
                disabled={!canNext}
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                className="px-1.5 rounded-md bg-coral-50 text-coral-500 disabled:opacity-30 hover:bg-coral-100 transition"
              >›</button>
            </div>

            <div className="flex-1 max-h-[260px] overflow-y-auto">
              <div className="grid grid-cols-5 gap-1">
                {(() => {
                  const maxSlots = Math.max(...days.map((d) => d.slots.length), 0);
                  if (maxSlots === 0) {
                    return (
                      <div className="col-span-5 py-8 text-center text-xs text-slate-400">
                        Sem horários disponíveis nestes dias
                      </div>
                    );
                  }
                  return Array.from({ length: maxSlots }).map((_, rowIdx) =>
                    days.map((d) => {
                      const slot = d.slots[rowIdx];
                      if (!slot) {
                        return <div key={`${d.date}-${rowIdx}`} className="text-center py-2 text-slate-300 text-sm">—</div>;
                      }
                      const isSel = selectedSlot === slot;
                      return (
                        <button
                          key={slot}
                          onClick={() => setSelectedSlot(slot)}
                          className={cn(
                            'py-2 rounded-md border text-xs font-medium transition',
                            isSel
                              ? 'bg-teal-500 text-white border-teal-500'
                              : 'bg-white border-slate-200 text-slate-700 hover:border-teal-400 hover:text-teal-600',
                          )}
                        >
                          {formatTime(slot)}
                        </button>
                      );
                    }),
                  );
                })()}
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-slate-200 space-y-2">
              <div className="text-xs text-slate-500 text-center sm:text-left truncate">
                {selectedSlot
                  ? new Date(selectedSlot).toLocaleString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })
                  : 'Selecione uma data'}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <ContactTherapistButton therapistId={t.id} variant="card" className="w-full h-10 rounded-full text-sm" />
                <Link
                  to={linkTo as any}
                  className={cn(
                    'inline-flex items-center justify-center h-10 rounded-full text-sm font-semibold transition',
                    selectedSlot
                      ? 'bg-teal-500 hover:bg-teal-600 text-white'
                      : 'bg-slate-200 text-slate-400 pointer-events-none',
                  )}
                >
                  Agendar
                </Link>
              </div>
            </div>
          </div>
        </div>

        <Link
          to={isLoggedIn ? `/terapeutas/${t.id}/agendar` as any : `/login?next=${encodeURIComponent(`/terapeutas/${t.id}/agendar`)}` as any}
          className="block bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-center py-3.5 font-semibold transition"
        >
          Estou disponível. Faça sua sessão agora!
        </Link>
      </CardContent>
    </Card>
  );
}
